import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Toolbar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
} from '@mui/material';
import { Add, Search, Factory } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { createWorkCenter, updateWorkCenter, WorkCenter } from '../../../store/slices/masterDataSlice';

const WC_TYPES: WorkCenter['type'][] = ['machine', 'manual', 'automated', 'inspection'];

const defaultForm: Partial<WorkCenter> = {
  code: '',
  name: '',
  type: 'manual',
  department: '',
  capacity: 8,
  capacityUom: 'hrs/day',
  status: 'active',
  description: '',
};

const WorkCentersTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { workCenters, workCentersLoading, error } = useSelector(
    (state: RootState) => state.masterData
  );

  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWC, setEditingWC] = useState<WorkCenter | null>(null);
  const [form, setForm] = useState<Partial<WorkCenter>>(defaultForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const filteredWorkCenters = workCenters.filter(
    (wc) =>
      !searchText ||
      wc.code.toLowerCase().includes(searchText.toLowerCase()) ||
      wc.name.toLowerCase().includes(searchText.toLowerCase()) ||
      wc.department.toLowerCase().includes(searchText.toLowerCase())
  );

  const openAddDialog = () => {
    setEditingWC(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (wc: WorkCenter) => {
    setEditingWC(wc);
    setForm({ ...wc });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.code?.trim()) errors.code = 'Code is required';
    if (!form.name?.trim()) errors.name = 'Name is required';
    if (!form.department?.trim()) errors.department = 'Department is required';
    if (!form.capacity || form.capacity <= 0) errors.capacity = 'Capacity must be > 0';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (editingWC) {
      await dispatch(updateWorkCenter({ id: editingWC.id, data: form }));
    } else {
      await dispatch(createWorkCenter(form));
    }
    setDialogOpen(false);
  };

  const getTypeColor = (type: WorkCenter['type']): 'primary' | 'secondary' | 'info' | 'warning' => {
    switch (type) {
      case 'machine': return 'primary';
      case 'manual': return 'secondary';
      case 'automated': return 'info';
      case 'inspection': return 'warning';
      default: return 'primary';
    }
  };

  const getStatusColor = (status: WorkCenter['status']): 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'maintenance': return 'warning';
      default: return 'success';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: 'Code',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    { field: 'name', headerName: 'Work Center Name', flex: 1, minWidth: 160 },
    {
      field: 'type',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={(params.value ?? '').toUpperCase()}
          color={getTypeColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    { field: 'department', headerName: 'Department', width: 140 },
    {
      field: 'capacity',
      headerName: 'Capacity',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value} {params.row.capacityUom}
        </Typography>
      ),
    },
    {
      field: 'utilizationPercent',
      headerName: 'Utilization',
      width: 150,
      renderCell: (params) => {
        const pct = params.value ?? 0;
        return (
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct > 90 ? 'error' : pct > 70 ? 'warning' : 'success'}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" sx={{ minWidth: 32 }}>
              {pct}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'linkedRoutingsCount',
      headerName: 'Routings',
      width: 90,
      type: 'number',
      renderCell: (params) => (
        <Chip label={params.value ?? 0} size="small" variant="outlined" />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={(params.value ?? '').toUpperCase()}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => openEditDialog(params.row)}
          key="edit"
        />,
      ],
    },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search work centers..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <Box flex={1} />
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
            Add Work Center
          </Button>
        </Toolbar>
      </Paper>

      <Paper sx={{ height: 500 }}>
        <DataGrid
          rows={filteredWorkCenters}
          columns={columns}
          loading={workCentersLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Factory />
            {editingWC ? `Edit Work Center: ${editingWC.code}` : 'Add Work Center'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Code *"
                value={form.code || ''}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                error={!!formErrors.code}
                helperText={formErrors.code}
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={form.type || 'manual'}
                  label="Type *"
                  onChange={(e) => setForm({ ...form, type: e.target.value as WorkCenter['type'] })}
                >
                  {WC_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Work Center Name *"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              size="small"
              fullWidth
            />
            <TextField
              label="Department *"
              value={form.department || ''}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              error={!!formErrors.department}
              helperText={formErrors.department}
              size="small"
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Capacity *"
                type="number"
                value={form.capacity ?? 8}
                onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
                error={!!formErrors.capacity}
                helperText={formErrors.capacity}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Capacity UOM"
                value={form.capacityUom || ''}
                onChange={(e) => setForm({ ...form, capacityUom: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
                placeholder="hrs/day, pcs/hr..."
              />
            </Box>
            <TextField
              label="Description"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status || 'active'}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value as WorkCenter['status'] })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingWC ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkCentersTable;
